// Canonical FX rate resolver.
//
// Every V2 screen that needs to show a "local equivalent" caption next to a
// reporting-currency figure MUST go through this module. The class of bug
// where two screens disagree on the same contract's local-equivalent is
// structural: each screen used to re-derive the rate from raw fields with
// slightly different precedence (commitment scalars only, work-plan only,
// activity only, etc.). Consolidating the derivation here — and forbidding
// raw field access in components via ESLint — eliminates that class of bug.
//
// Direction convention (single source of truth):
//   rate = local units per ONE unit of reporting currency.
//   restate reporting → local : multiply by rate
//   restate local → reporting : divide by rate
//
// Every resolver returns either a fully-typed `FxRate` (with a `source` tag
// naming where the rate came from, for tooltips/telemetry/tests) or `null`
// when no rate is derivable. Callers MUST render nothing rather than
// fabricating a figure when the resolver returns null.

import type { PlanningActivity, Project, SpendPlan, V2Contract, WorkPlan } from '../@types';
import { getContractAmountForCurrency } from '../components/pages/ProjectV2/Contracts/Contracts.utils';

/**
 * Where a derived rate came from. Surfaced in `<LocalEquivalent>` tooltips
 * and exercised by snapshot tests so every page on the same entity reports
 * the same source.
 */
export type FxSource =
	| 'commitment-scalar'
	| 'contract-amounts'
	| 'activity'
	| 'work-plan'
	| 'spend-plan-work-plan'
	| 'voucher-captured';

export interface FxRate {
	/** Local currency units per 1 unit of reporting currency. Always > 0. */
	rate: number;
	source: FxSource;
	reportingCurrency: string;
	localCurrency: string;
	/** The pair of amounts the rate was derived from, for tooltip display. */
	basis: { amount: number; amountLocal: number };
}

/** Project currencies are loaded with type tags; centralise the lookup. */
const pickCurrency = (
	project: { currencies?: { currency: string; type: string }[] } | null | undefined,
	type: 'REPORTING' | 'LOCAL'
): string | null => project?.currencies?.find((c) => c.type === type)?.currency ?? null;

export const getProjectReportingCurrency = (project: Project | null | undefined): string | null =>
	pickCurrency(project, 'REPORTING');

export const getProjectLocalCurrency = (project: Project | null | undefined): string | null =>
	pickCurrency(project, 'LOCAL');

const buildRate = (
	amount: number,
	amountLocal: number,
	reportingCurrency: string,
	localCurrency: string,
	source: FxSource
): FxRate | null => {
	if (!amount || amountLocal <= 0 || reportingCurrency === localCurrency) return null;
	const rate = amountLocal / amount;
	if (!Number.isFinite(rate) || rate <= 0) return null;
	return { rate, source, reportingCurrency, localCurrency, basis: { amount, amountLocal } };
};

/**
 * Resolve a work plan's implied FX rate (`amountLocal / amount`).
 *
 * Use this on work-plan-scoped pages (WorkPlan detail, SpendPlan, etc.). For
 * contract-scoped pages prefer `resolveContractFxRate` so the contract's own
 * amount rows win over the work plan when they disagree.
 */
export const resolveWorkPlanFxRate = (
	workPlan: WorkPlan | null | undefined,
	project: Project | null | undefined
): FxRate | null => {
	if (!workPlan) return null;
	const reportingCurrency = workPlan.currency;
	const localCurrency = workPlan.currencyLocal ?? getProjectLocalCurrency(project);
	if (!reportingCurrency || !localCurrency || workPlan.amountLocal == null) return null;
	return buildRate(
		workPlan.amount,
		workPlan.amountLocal,
		reportingCurrency,
		localCurrency,
		'work-plan'
	);
};

/**
 * Resolve a planning activity's implied FX rate.
 *
 * Each activity stores its own (amount, amountLocal) pair. This is the
 * authoritative rate for activity-scoped displays (PlanningActivity detail).
 */
export const resolveActivityFxRate = (
	activity: PlanningActivity | null | undefined,
	project: Project | null | undefined
): FxRate | null => {
	if (!activity) return null;
	const reportingCurrency = activity.currency;
	const localCurrency = activity.currencyLocal ?? getProjectLocalCurrency(project);
	if (!reportingCurrency || !localCurrency || activity.amountLocal == null) return null;
	return buildRate(
		activity.amount,
		activity.amountLocal,
		reportingCurrency,
		localCurrency,
		'activity'
	);
};

/**
 * Resolve a contract's implied FX rate, falling back to the work plan when
 * the contract carries no usable basis.
 *
 * Precedence (first hit wins):
 *   1. `commitment-scalar`  — operator-entered commitment override pair
 *   2. `contract-amounts`   — sum of contract.amounts[] rows by currency
 *   3. `work-plan`          — last-resort fallback when the contract has
 *                             nothing of its own
 *
 * Returns null when no basis is derivable anywhere. Never fabricates.
 */
export const resolveContractFxRate = (
	contract: V2Contract | null | undefined,
	workPlan: WorkPlan | null | undefined,
	project: Project | null | undefined
): FxRate | null => {
	if (!contract) return null;
	const reportingCurrency = workPlan?.currency ?? getProjectReportingCurrency(project);
	const localCurrency = workPlan?.currencyLocal ?? getProjectLocalCurrency(project);
	if (!reportingCurrency || !localCurrency || reportingCurrency === localCurrency) return null;

	// 1. Commitment scalars.
	if (
		contract.commitmentAmount != null &&
		contract.commitmentAmount > 0 &&
		contract.commitmentAmountLocal != null &&
		contract.commitmentAmountLocal > 0
	) {
		const r = buildRate(
			contract.commitmentAmount,
			contract.commitmentAmountLocal,
			reportingCurrency,
			localCurrency,
			'commitment-scalar'
		);
		if (r) return r;
	}

	// 2. Contract amount rows.
	const reportingAmount = getContractAmountForCurrency(contract, reportingCurrency);
	const localAmount = getContractAmountForCurrency(contract, localCurrency);
	if (reportingAmount != null && reportingAmount > 0 && localAmount != null && localAmount > 0) {
		const r = buildRate(
			reportingAmount,
			localAmount,
			reportingCurrency,
			localCurrency,
			'contract-amounts'
		);
		if (r) return r;
	}

	// 3. Work plan fallback.
	return resolveWorkPlanFxRate(workPlan, project);
};

/**
 * Spend plans don't carry their own FX — they inherit it from their parent
 * work plan. The source tag is reported as `spend-plan-work-plan` so the
 * tooltip on a spend-plan-scoped page makes the inheritance explicit.
 */
export const resolveSpendPlanFxRate = (
	spendPlan: SpendPlan | null | undefined,
	workPlan: WorkPlan | null | undefined,
	project: Project | null | undefined
): FxRate | null => {
	if (!spendPlan) return null;
	const wp = workPlan ?? (typeof spendPlan.workPlan === 'object' ? spendPlan.workPlan : null);
	const inherited = resolveWorkPlanFxRate(wp, project);
	if (!inherited) return null;
	return { ...inherited, source: 'spend-plan-work-plan' };
};

/**
 * Restate a reporting-currency value as local using a resolved rate.
 * Returns null when the rate is null, so callers can render an em dash.
 */
export const restateAsLocal = (reportingValue: number, fx: FxRate | null): number | null => {
	if (!fx) return null;
	return reportingValue * fx.rate;
};

/**
 * Restate a local-currency value as reporting using a resolved rate.
 */
export const restateAsReporting = (localValue: number, fx: FxRate | null): number | null => {
	if (!fx) return null;
	return localValue / fx.rate;
};

/**
 * Human-readable label for the rate's source, used by `<LocalEquivalent>`
 * tooltips and by the dev/QA inconsistency warnings.
 */
export const describeFxSource = (fx: FxRate): string => {
	const formatted = fx.rate.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 6,
	});
	const pair = `${formatted} ${fx.localCurrency} per 1 ${fx.reportingCurrency}`;
	switch (fx.source) {
		case 'commitment-scalar':
			return `Rate from contract commitment: ${pair}`;
		case 'contract-amounts':
			return `Rate from contract amounts: ${pair}`;
		case 'activity':
			return `Rate from activity budget: ${pair}`;
		case 'work-plan':
			return `Rate from work plan budget: ${pair}`;
		case 'spend-plan-work-plan':
			return `Rate inherited from work plan: ${pair}`;
		case 'voucher-captured':
			return `Rate captured on voucher: ${pair}`;
	}
};
